import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { StreamPanel, ResultPanel, FindingsPreview } from "@/components/review/ReviewOutput";
import { DocumentViewer, ReviewControls, RunButtons } from "@/components/review/ReviewControls";
import { useReviewStream } from "@/hooks/useReviewStream";
import { useReviewSetup } from "@/hooks/useReviewSetup";

export default function AIReview() {
  const { id } = useParams();
  const { activeWorkspaceId } = useAuth();
  const s = useReviewSetup(id, activeWorkspaceId);
  const [mode, setMode] = useState("steel_spec");
  const [provider, setProvider] = useState("anthropic");
  const [custom, setCustom] = useState("");
  const stream = useReviewStream({ projectId: id, onFindings: s.setFindings, body: () => ({ project_id: id, document_ids: s.selectedDocs, mode, custom_instructions: custom, playbook_ids: s.selectedPBs, model_provider: provider }) });
  const resumed = useRef(false);
  useEffect(() => {
    // Returning to the page while a run is still executing server-side: reattach instead of starting a duplicate.
    if (resumed.current) return;
    resumed.current = true;
    api.get(`/projects/${id}/reviews`).then(({ data }) => {
      const active = (data || []).find((r) => r.status === "running");
      if (active) { toast.info("A review is already running for this project — reconnected to its progress."); stream.resume(active.id); }
    }).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    if (!s.selectedDocs.length) return toast.error("Select at least one document");
    if (stream.running) return;
    const { data } = await api.get(`/projects/${id}/reviews`).catch(() => ({ data: [] }));
    const active = (data || []).find((r) => r.status === "running");
    if (active) { toast.info("A review is already running for this project — reconnected instead of starting another."); return stream.resume(active.id); }
    stream.runStream();
  };

  return (
    <div className="p-6 max-w-[1800px] mx-auto" data-testid="ai-review-page">
      <div className="mb-4">
        <div className="label-cap text-amber-500">// AI REVIEW ENGINE</div>
        <h1 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-100 mt-1">Split-Screen Document Review</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <DocumentViewer docs={s.docs} viewingDoc={s.viewingDoc} onView={s.viewDoc} />
        <div className="lg:col-span-6 space-y-4">
          <ReviewControls s={s} mode={mode} setMode={setMode} provider={provider} setProvider={setProvider} custom={custom} setCustom={setCustom}>
            <RunButtons running={stream.running} canRun={s.selectedDocs.length > 0} onRun={run} onCancel={stream.cancel} />
          </ReviewControls>
          <StreamPanel running={stream.running} streamText={stream.streamText} />
          <ResultPanel result={stream.result} />
          <FindingsPreview findings={s.findings} />
        </div>
      </div>
    </div>
  );
}
